import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const TIER_CHIP = {
  1: "T1",
  2: "T2",
  3: "T3",
};

/**
 * Debounced username typeahead used by the Browse "Seller" filter.
 *
 * Value is the raw username string (no leading "@"). Suggestions come from
 * `GET /api/users/search?q=…` which is scoped to vendors + admins + founders,
 * so buyer-only accounts are never surfaced. Keyboard support: ↑ ↓ Enter Esc.
 */
export default function SellerTypeahead({ value, onChange, testIdPrefix = "sidebar-seller" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const abortRef = useRef(null);

  // Debounced fetch on value change.
  useEffect(() => {
    const cleaned = (value || "").trim().replace(/^@+/, "");
    if (cleaned.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(async () => {
      // Cancel any in-flight previous request.
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      try {
        const { data } = await api.get("/users/search", {
          params: { q: cleaned, limit: 8 },
          signal: controller.signal,
        });
        setSuggestions(data.users || []);
        setHighlight(0);
      } catch (err) {
        if (err?.name !== "CanceledError" && err?.name !== "AbortError") {
          console.error("seller search failed", err);
        }
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [value]);

  // Close the dropdown on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (u) => {
    onChange(u.username);
    setOpen(false);
    setSuggestions([]);
  };

  const onKey = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => (h + 1) % suggestions.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length); }
    else if (e.key === "Enter") { e.preventDefault(); pick(suggestions[highlight]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <input
        data-testid={testIdPrefix}
        className="h57-input !py-2 text-sm"
        placeholder="e.g. triodad78"
        value={value}
        onFocus={() => setOpen(true)}
        onChange={(e) => { onChange(e.target.value.replace(/^@+/, "")); setOpen(true); }}
        onKeyDown={onKey}
        autoComplete="off"
        spellCheck={false}
      />
      {open && (value || "").trim().length >= 2 && (
        <div
          data-testid={`${testIdPrefix}-dropdown`}
          className="absolute z-30 left-0 right-0 mt-1 bg-[#0A0A0A] border border-white/15 shadow-xl max-h-72 overflow-y-auto"
        >
          {loading && suggestions.length === 0 && (
            <div className="px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-zinc-500">Searching…</div>
          )}
          {!loading && suggestions.length === 0 && (
            <div className="px-3 py-2 font-mono text-[10px] tracking-widest uppercase text-zinc-500">No sellers found</div>
          )}
          {suggestions.map((u, idx) => (
            <button
              key={u.username}
              type="button"
              data-testid={`${testIdPrefix}-option-${u.username}`}
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => { e.preventDefault(); pick(u); }}
              className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 text-sm border-b border-white/5 last:border-b-0 ${idx === highlight ? "bg-[#FF5F1F]/10 text-white" : "text-zinc-200 hover:bg-white/5"}`}
            >
              <span className="font-mono text-[13px]">
                @<span className="font-semibold">{u.username}</span>
              </span>
              <span className="flex items-center gap-1">
                {u.role === "admin" && <span className="h57-chip !py-0 !px-1.5 !text-[9px] !border-[#FF5F1F] !text-[#FF5F1F]">Admin</span>}
                {u.founding_member && <span className="h57-chip h57-chip-gold !py-0 !px-1.5 !text-[9px]">Founding</span>}
                {u.is_vendor && u.vendor_tier && (
                  <span className={`h57-chip !py-0 !px-1.5 !text-[9px] ${u.vendor_tier === 3 ? "h57-chip-gold" : u.vendor_tier === 2 ? "h57-chip-orange" : ""}`}>
                    {TIER_CHIP[u.vendor_tier]}
                  </span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
